import type { FunctionComponent } from 'react';
import { cloneElement, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import Loader from '../generic/Loader';
import { useAsyncValue } from '../../hooks';
import { get } from '../../redux/api';
import type { FailedFetchResponse } from '../../helpers/errors';
import { errorMessagesListFromResponse } from '../../helpers/errors';
import type { Revision } from '../../types';
import { nuc } from '../../utils';

type Props = {
  revision: string;
  title?: string;
  show: boolean;
  onHide: () => void;
};

/**
 * RevisionModalWrapper provides a wrapper for legacy modals that require revision data, but do not load that data
 * within the modal. This wrapper will load the revision data and then pass it to the child modal while presenting a loading screen
 * @example
 * <RevisionModalWrapper
 *   revision={revision}
 *   title="Loading dependency"
 *   show={isOpen}
 *   onHide={onClose}>
 *    // Revision object is passed automatically to the child modal
 *    <ModalEditDependency
 *     show={isOpen}
 *     onHide={onClose}
 *    >
 * </RevisionModalWrapper>
 */
const RevisionModalWrapper: FunctionComponent<Props> = ({
  revision,
  title = 'Loading...',
  show,
  onHide,
  children,
}) => {
  const { value, isLoading, errors, ...actions } = useAsyncValue<Revision>(null);

  const shouldShowChildModal = show && value && !isLoading;
  const shouldShowRevisionModal = show ? !shouldShowChildModal : false;

  useEffect(() => {
    async function fetchRevision() {
      try {
        actions.setPending();
        const { data } = await get<Revision>(`/api/revisions/${nuc(revision)}`);
        if (!data) {
          throw new Error('Could not find revision data');
        }
        actions.setResolve(data);
      } catch (error: unknown) {
        actions.setReject(errorMessagesListFromResponse(error as FailedFetchResponse));
      }
    }

    // If not showing, or revision is missing, don't fetch
    if (!show || !revision) {
      return;
    }

    fetchRevision();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revision]);

  return (
    <>
      <Modal show={shouldShowRevisionModal} onHide={onHide} backdrop animation>
        <Modal.Header closeButton={!!errors}>
          <Modal.Title>
            {title}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {errors ?
            <div className="alert alert-error">{errors}</div> :
            <Loader />}
        </Modal.Body>
      </Modal>
      {shouldShowChildModal && cloneElement(
        children,
        { revision: value },
      )}
    </>
  );
};

export default RevisionModalWrapper;
